import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  X,
  ShoppingBag,
  MagnifyingGlass,
  CloudArrowUp,
  CircleNotch,
  CheckCircle,
  Warning,
} from "@phosphor-icons/react";
import { t } from "../i18n";
import {
  listShopifyConnections,
  searchShopifyResources,
  applyShopifyRewrites,
} from "../lib/api";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

const RESOURCE_TYPES = [
  { value: "product", labelKey: "res_product" },
  { value: "page", labelKey: "res_page" },
  { value: "custom_collection", labelKey: "res_custom_collection" },
  { value: "smart_collection", labelKey: "res_smart_collection" },
];

const FIELDS = [
  { value: "title", labelKey: "shopify_fields_title" },
  { value: "body_html", labelKey: "shopify_fields_body" },
  { value: "seo_title", labelKey: "shopify_fields_seo_title" },
  { value: "seo_description", labelKey: "shopify_fields_seo_desc" },
];

/**
 * Reusable modal that pushes a single rewrite (original_phrase → suggested_phrase)
 * to a Shopify resource as a find-and-replace within the selected field.
 *
 * Props:
 *   open: bool
 *   onClose: () => void
 *   lang: string
 *   originalPhrase: string
 *   suggestedPhrase: string
 *   onApplied?: (result) => void
 */
export default function ApplyToShopifyModal({
  open,
  onClose,
  lang,
  originalPhrase,
  suggestedPhrase,
  onApplied,
}) {
  const [conns, setConns] = useState([]);
  const [loadingConns, setLoadingConns] = useState(false);
  const [connId, setConnId] = useState("");
  const [resourceType, setResourceType] = useState("product");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedResource, setSelectedResource] = useState(null);
  const [field, setField] = useState("body_html");
  const [applying, setApplying] = useState(false);
  const [response, setResponse] = useState(null);

  // Load connections when opened
  useEffect(() => {
    if (!open) return;
    setLoadingConns(true);
    listShopifyConnections()
      .then((list) => {
        setConns(list || []);
        if ((list || []).length && !connId) setConnId(list[0].id);
      })
      .catch((e) => toast.error(e?.response?.data?.detail || "Error"))
      .finally(() => setLoadingConns(false));
  }, [open, connId]);

  // Reset transient state every time the modal is opened
  useEffect(() => {
    if (open) {
      setSelectedResource(null);
      setResults([]);
      setQuery("");
      setResponse(null);
      setField("body_html");
    }
  }, [open]);

  // Auto-find: when modal opens with originalPhrase, fire a verbatim search across products+pages
  // and auto-select if exactly one match is found.
  useEffect(() => {
    if (!open || !connId || !originalPhrase || originalPhrase.length < 5) return;
    let cancelled = false;
    (async () => {
      setSearching(true);
      try {
        const list = await searchShopifyResources(connId, {
          resourceType,
          text: originalPhrase,
          limit: 10,
        });
        if (cancelled) return;
        setResults(list || []);
        if ((list || []).length === 1) {
          setSelectedResource(list[0]);
        }
      } catch { /* silent: fallback to manual */ }
      finally { if (!cancelled) setSearching(false); }
    })();
    return () => { cancelled = true; };
  }, [open, connId, resourceType, originalPhrase]);

  // Debounced live search (manual)
  const runSearch = useCallback(async () => {
    if (!connId) return;
    setSearching(true);
    try {
      const list = await searchShopifyResources(connId, {
        resourceType,
        q: query,
        limit: 20,
      });
      setResults(list || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setSearching(false);
    }
  }, [connId, resourceType, query]);

  useEffect(() => {
    if (!open || !connId) return;
    if (!query) return; // skip — let auto-find handle the initial state
    const timer = setTimeout(runSearch, 300);
    return () => clearTimeout(timer);
  }, [open, connId, resourceType, query, runSearch]);

  const apply = async () => {
    if (!selectedResource || !connId) return;
    setApplying(true);
    setResponse(null);
    try {
      const res = await applyShopifyRewrites({
        connection_id: connId,
        resource_type: selectedResource.resource_type,
        resource_id: selectedResource.resource_id,
        rewrites: [
          {
            field,
            original_phrase: originalPhrase,
            suggested_phrase: suggestedPhrase,
          },
        ],
      });
      setResponse(res);
      if (res.error) {
        toast.error(res.error);
      } else if ((res.updated_fields || []).length === 0) {
        toast.error(t(lang, "apply_phrase_not_found"));
      } else {
        toast.success(t(lang, "apply_success"));
        if (onApplied) onApplied(res);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setApplying(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-neutral-900/60 backdrop-blur-sm p-4"
      data-testid="apply-shopify-modal"
      onClick={onClose}
    >
      <div
        className="w-full max-w-3xl max-h-[90vh] overflow-auto bg-white border border-neutral-900 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center gap-2 px-5 py-3 border-b border-neutral-900 bg-neutral-900 text-white">
          <ShoppingBag size={14} weight="fill" />
          <div className="text-xs uppercase tracking-[0.2em] font-mono">
            {t(lang, "apply_modal_title")}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="ml-auto text-white/80 hover:text-white"
            data-testid="apply-modal-close"
            aria-label="close"
          >
            <X size={16} />
          </button>
        </div>

        {/* Snippet preview */}
        <div className="px-5 py-4 border-b border-neutral-200 bg-neutral-50">
          <div className="text-[10px] uppercase tracking-[0.2em] font-mono text-neutral-500 mb-2">
            {t(lang, "apply_modal_snippet")}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="border border-red-300 bg-red-50">
              <div className="px-2 py-1 border-b border-red-300 text-[10px] font-mono uppercase tracking-widest text-red-800">
                {t(lang, "original")}
              </div>
              <pre className="p-2 text-[11px] font-mono whitespace-pre-wrap text-red-900 max-h-24 overflow-auto">
                {originalPhrase}
              </pre>
            </div>
            <div className="border border-emerald-400 bg-emerald-50">
              <div className="px-2 py-1 border-b border-emerald-400 text-[10px] font-mono uppercase tracking-widest text-emerald-800">
                {t(lang, "suggested")}
              </div>
              <pre className="p-2 text-[11px] font-mono whitespace-pre-wrap text-emerald-900 max-h-24 overflow-auto">
                {suggestedPhrase}
              </pre>
            </div>
          </div>
        </div>

        {/* Step 1: connection */}
        <div className="px-5 py-4 space-y-4">
          {loadingConns ? (
            <div className="text-xs font-mono text-neutral-500">…</div>
          ) : conns.length === 0 ? (
            <div className="border border-amber-500 bg-amber-50 p-3 text-xs font-mono text-amber-800" data-testid="apply-no-conn">
              {t(lang, "apply_no_conn")}
            </div>
          ) : (
            <>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.2em] font-mono text-neutral-500 mb-1.5">
                  {t(lang, "apply_modal_connection")}
                </label>
                <Select value={connId} onValueChange={setConnId}>
                  <SelectTrigger
                    className="h-10 w-full rounded-none border border-neutral-900 font-mono text-sm shadow-none"
                    data-testid="apply-conn-select"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-none border border-neutral-900 shadow-none">
                    {conns.map((c) => (
                      <SelectItem key={c.id} value={c.id} className="rounded-none font-mono">
                        {c.name} — {c.store_url}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Step 2: resource type + search */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.2em] font-mono text-neutral-500 mb-1.5">
                    {t(lang, "apply_modal_resource_type")}
                  </label>
                  <Select value={resourceType} onValueChange={(v) => { setResourceType(v); setSelectedResource(null); }}>
                    <SelectTrigger
                      className="h-10 w-full rounded-none border border-neutral-900 font-mono text-sm shadow-none"
                      data-testid="apply-restype-select"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-none border border-neutral-900 shadow-none">
                      {RESOURCE_TYPES.map((rt) => (
                        <SelectItem key={rt.value} value={rt.value} className="rounded-none font-mono">
                          {t(lang, rt.labelKey)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-[10px] uppercase tracking-[0.2em] font-mono text-neutral-500 mb-1.5">
                    {t(lang, "apply_modal_search")}
                  </label>
                  <div className="flex items-center border border-neutral-900 bg-white">
                    <MagnifyingGlass size={14} className="ml-3 text-neutral-500" />
                    <input
                      type="text"
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      placeholder={t(lang, "apply_modal_search_ph")}
                      className="flex-1 px-3 py-2 text-sm font-mono outline-none bg-transparent"
                      data-testid="apply-search-input"
                    />
                    {searching && <CircleNotch size={14} className="mr-3 animate-spin text-neutral-500" />}
                  </div>
                </div>
              </div>

              {/* Results list */}
              <div className="border border-neutral-300 bg-white max-h-56 overflow-auto" data-testid="apply-results-list">
                {results.length === 0 && !searching && (
                  <div className="p-3 text-[11px] font-mono text-neutral-500">
                    {t(lang, "apply_modal_no_results")}
                  </div>
                )}
                {results.map((r) => {
                  const isSelected = selectedResource?.resource_id === r.resource_id
                    && selectedResource?.resource_type === r.resource_type;
                  return (
                    <button
                      type="button"
                      key={`${r.resource_type}:${r.resource_id}`}
                      onClick={() => setSelectedResource(r)}
                      className={`w-full text-left px-3 py-2 border-t border-neutral-100 ma-trans ${
                        isSelected
                          ? "bg-emerald-50 border-l-4 border-l-emerald-600"
                          : "hover:bg-neutral-50"
                      }`}
                      data-testid={`apply-result-${r.resource_id}`}
                    >
                      <div className="text-sm text-neutral-900 truncate">{r.title || "—"}</div>
                      <div className="text-[10px] font-mono text-neutral-500 truncate">
                        {r.handle ? `/${r.handle}` : `#${r.resource_id}`}
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Step 3: field */}
              {selectedResource && (
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.2em] font-mono text-neutral-500 mb-1.5">
                    {t(lang, "apply_modal_field")}
                  </label>
                  <Select value={field} onValueChange={setField}>
                    <SelectTrigger
                      className="h-10 w-full rounded-none border border-neutral-900 font-mono text-sm shadow-none"
                      data-testid="apply-field-select"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-none border border-neutral-900 shadow-none">
                      {FIELDS.map((f) => (
                        <SelectItem key={f.value} value={f.value} className="rounded-none font-mono">
                          {t(lang, f.labelKey)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] font-mono text-neutral-500 mt-1.5 leading-relaxed">
                    {t(lang, "apply_modal_field_hint")}
                  </p>
                </div>
              )}

              {/* Apply result */}
              {response && (
                <div
                  className={`border p-3 text-xs leading-relaxed ${
                    response.error || (response.updated_fields || []).length === 0
                      ? "border-amber-500 bg-amber-50 text-amber-900"
                      : "border-emerald-500 bg-emerald-50 text-emerald-900"
                  }`}
                  data-testid="apply-result-banner"
                >
                  <div className="flex items-center gap-2 font-mono uppercase tracking-widest text-[10px]">
                    {response.error || (response.updated_fields || []).length === 0
                      ? <><Warning size={12} weight="fill" /> {t(lang, "apply_warn_title")}</>
                      : <><CheckCircle size={12} weight="fill" /> {t(lang, "apply_done_title")}</>}
                  </div>
                  <div className="mt-1">
                    {response.error
                      || ((response.updated_fields || []).length > 0
                          ? `${t(lang, "apply_updated_fields")}: ${response.updated_fields.join(", ")}`
                          : t(lang, "apply_phrase_not_found"))}
                  </div>
                  {response.replacements_count && Object.keys(response.replacements_count).length > 0 && (
                    <div className="mt-1.5 text-[11px] font-mono" data-testid="apply-replacements-count">
                      {t(lang, "apply_replacements_count")}: {Object.entries(response.replacements_count).map(([f, n]) => `${f}=${n}`).join(", ")}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center gap-2 px-5 py-3 border-t border-neutral-200 bg-neutral-50">
          <button
            type="button"
            onClick={onClose}
            className="border border-neutral-900 bg-white text-neutral-900 hover:bg-neutral-100 px-4 py-2 text-xs uppercase tracking-widest font-mono ma-trans"
            data-testid="apply-modal-cancel"
          >
            {t(lang, "cancel")}
          </button>
          <button
            type="button"
            onClick={apply}
            disabled={!selectedResource || applying || conns.length === 0}
            className="ml-auto inline-flex items-center gap-2 bg-emerald-700 text-white hover:bg-emerald-800 disabled:opacity-50 disabled:cursor-not-allowed px-4 py-2 text-xs uppercase tracking-widest font-medium ma-trans"
            data-testid="apply-modal-submit"
          >
            {applying ? <CircleNotch size={12} className="animate-spin" /> : <CloudArrowUp size={12} />}
            {applying ? t(lang, "apply_modal_applying") : t(lang, "apply_modal_submit")}
          </button>
        </div>
      </div>
    </div>
  );
}
